import React from 'react';
import { ArrowRight, Car, Home, Building2, Briefcase, CircleDollarSign, GraduationCap, Heart, Gift, Clock, Umbrella } from 'lucide-react';

const goals = [
  {
    id: 'marriage-goal',
    title: 'Marriage Goal',
    icon: Heart,
    description: 'Plan your finances for the perfect wedding and starting your new life together with confidence.',
    timeframe: '1-5 Years',
    riskProfile: 'Low to Medium',
    minInvestment: '₹15L'
  },
  {
    id: 'education-goal',
    title: 'Education Goal',
    icon: GraduationCap,
    description: 'Secure your children\'s future with strategic investments for quality education in India or abroad.',
    timeframe: '5-15 Years',
    riskProfile: 'Medium',
    minInvestment: '₹20L'
  },
  {
    id: 'car-purchase',
    title: 'Car Goal',
    icon: Car,
    description: 'Strategic investment plans for your dream car, considering depreciation and optimal financing.',
    timeframe: '2-5 Years',
    riskProfile: 'Low to Medium',
    minInvestment: '₹10L'
  },
  {
    id: 'bike-goal',
    title: 'Bike Goal',
    icon: Clock,
    description: 'Achieve your dream of owning that perfect bike through smart and systematic investments.',
    timeframe: '1-3 Years',
    riskProfile: 'Low',
    minInvestment: '₹2L'
  },
  {
    id: 'home-ownership',
    title: 'Home Ownership',
    icon: Building2,
    description: 'Long-term investment strategy to accumulate the corpus needed for your dream home.',
    timeframe: '7-15 Years',
    riskProfile: 'Medium to High',
    minInvestment: '₹50L'
  },
  {
    id: 'retirement-goal',
    title: 'Retirement Goal',
    icon: Umbrella,
    description: 'Build a substantial corpus to ensure a comfortable and financially independent retirement life.',
    timeframe: '15-30 Years',
    riskProfile: 'Medium to High',
    minInvestment: '₹30L'
  },

  // Commented out goals
  /*
  {
    id: 'home-loan',
    title: 'Home Loan Repayment',
    icon: Home,
    description: 'Accelerate your home loan closure through structured investments and tax-efficient planning.',
    timeframe: '5-15 Years',
    riskProfile: 'Medium',
    minInvestment: '₹25L'
  },
  {
    id: 'business',
    title: 'Starting A Business',
    icon: Briefcase,
    description: 'Build your capital base systematically for your entrepreneurial journey.',
    timeframe: '5-10 Years',
    riskProfile: 'High',
    minInvestment: '₹40L'
  },
  {
    id: 'gold-loan',
    title: 'Gold Loan Foreclosure',
    icon: CircleDollarSign,
    description: 'Strategic planning to release your gold collateral through systematic investments.',
    timeframe: '1-3 Years',
    riskProfile: 'Low',
    minInvestment: '₹5L'
  }
  */
];

const GoalCard = ({ goal }) => {
  const { icon: Icon, title, description, timeframe, riskProfile, minInvestment, id } = goal;

  const handleLearnMore = () => {
    switch (id) {
      case 'car-purchase':
        window.location.href = 'https://club.maxiomwealth.com/goals/add?goal_type=CAR';
        break;
      case 'home-ownership':
        window.location.href = 'https://club.maxiomwealth.com/goals/add?goal_type=HME';
        break;
      case 'education-goal':
        window.location.href = 'https://club.maxiomwealth.com/goals/add?goal_type=HE';
        break;
      case 'marriage-goal':
        window.location.href = 'https://club.maxiomwealth.com/goals/add?goal_type=CMR';
        break;
      case 'bike-goal':
        window.location.href = 'https://club.maxiomwealth.com/goals/add?goal_type=BK';
        break;
      case 'retirement-goal':
        window.location.href = 'https://club.maxiomwealth.com/Goals/BasicDetails?type=RETIREMENT&from';
        break;
      default:
        break;
    }
  };

  return (
    <div id="goaltype" className="group relative">
      {/* Card Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#113262] to-[#1C52A0] rounded-2xl transform transition-transform duration-300 group-hover:scale-[1.02]" />

      {/* Card Content */}
      <div className="relative p-8 h-full flex flex-col">
        {/* Icon Container */}
        <div className="mb-6">
          <div className="bg-[#F49611] w-14 h-14 rounded-xl flex items-center justify-center shadow-lg">
            <Icon className="w-7 h-7 text-white" />
          </div>
        </div>

        {/* Content */}
        <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
        <p className="text-white/80 mb-6 flex-grow">{description}</p>

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-6">
          <div>
            <div className="text-white/60 text-sm mb-1">Timeframe</div>
            <div className="text-white font-semibold">{timeframe}</div>
          </div>
          <div>
            <div className="text-white/60 text-sm mb-1">Risk Profile</div>
            <div className="text-white font-semibold">{riskProfile}</div>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleLearnMore}
          className="mt-6 w-full bg-[#F49611] text-white hover:bg-orange-500
 py-3 rounded-xl font-semibold transition-colors duration-300"
        >
          Explore Plan
        </button>
      </div>
    </div>
  );
};

const GoalTypesSection = () => {
  return (
    <div className="bg-white pb-10">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-12 h-0.5 bg-[#F49611]"></div>
              <span className="text-[#F49611] font-medium uppercase tracking-wider">Investment Goals</span>
              <div className="w-12 h-0.5 bg-[#F49611]"></div>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
            Transform Your Dreams into Reality
          </h2>
          <p className="text-xl text-gray-600">
            Explore our comprehensive range of goal-based investment strategies,
            each crafted to align with your unique life objectives.
          </p>
        </div>

        {/* Goals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {goals.slice(0, 6).map((goal) => (
            <GoalCard key={goal.id} goal={goal} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16">
          <div className="flex justify-center">
            <a href='https://club.maxiomwealth.com/goals/finplan/create' className="w-auto px-8 py-4 bg-white border-2 border-[#113262] text-[#113262] hover:bg-[#E8F0FE] hover:text-[#113262] text-lg font-medium rounded-lg transition-all duration-300 flex items-center gap-2">
              Browse All Goals
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalTypesSection;